import { useEffect, useState } from "react";
import { COMPANY_INFO, HAS_LEGAL_PLACEHOLDERS } from "@/lib/companyInfo";
import { BETA_MODE } from "@/lib/betaMode";
import { supabase } from "@/integrations/supabase/client";

/**
 * SeoHead — runtime <head> sync from COMPANY_INFO + dynamic structured data.
 *
 * Why a React component instead of editing index.html?
 *  - index.html is static; it cannot import TS values or query the DB.
 *  - Googlebot executes JS, so runtime-injected JSON-LD is indexed.
 *
 * What this does on mount:
 *  1. Updates <meta property="og:site_name"> and <meta name="application-name">.
 *  2. Injects Organization JSON-LD (only when legal data is real).
 *  3. Injects SoftwareApplication JSON-LD with offers pulled live from
 *     subscription_tiers (lowPrice/highPrice/priceRange). Skipped while in
 *     BETA_MODE since payments are disabled and prices aren't real yet.
 */
const ORG_SCRIPT_ID = "ld-organization";
const APP_SCRIPT_ID = "ld-software-application";

type TierRow = { name: string; slug: string; price_ron: number };
type ReviewRow = { rating: number };

const SeoHead = () => {
  const [tiers, setTiers] = useState<TierRow[] | null>(null);
  const [reviews, setReviews] = useState<ReviewRow[] | null>(null);

  // Pull active tiers + approved reviews once.
  // Tiers skipped in beta (placeholder prices).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const tierPromise = BETA_MODE
        ? Promise.resolve(null)
        : supabase
            .from("subscription_tiers")
            .select("name, slug, price_ron")
            .eq("is_active", true)
            .order("sort_order", { ascending: true });

      const reviewPromise = supabase
        .from("reviews" as any)
        .select("rating")
        .eq("approved", true);

      const [tierRes, reviewRes] = await Promise.all([
        tierPromise,
        reviewPromise,
      ]);

      if (cancelled) return;
      if (tierRes && !tierRes.error && tierRes.data) {
        setTiers(tierRes.data as TierRow[]);
      }
      if (!reviewRes.error && reviewRes.data) {
        setReviews(reviewRes.data as unknown as ReviewRow[]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    // 1) Keep og:site_name + application-name in sync with brandName
    const ogSiteName = document.querySelector(
      'meta[property="og:site_name"]'
    ) as HTMLMetaElement | null;
    if (ogSiteName) ogSiteName.content = COMPANY_INFO.brandName;

    let appName = document.querySelector(
      'meta[name="application-name"]'
    ) as HTMLMetaElement | null;
    if (!appName) {
      appName = document.createElement("meta");
      appName.name = "application-name";
      document.head.appendChild(appName);
    }
    appName.content = COMPANY_INFO.brandName;

    // 2) Organization JSON-LD — only emit when legal data is real
    document.getElementById(ORG_SCRIPT_ID)?.remove();
    if (!HAS_LEGAL_PLACEHOLDERS) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = ORG_SCRIPT_ID;
      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        name: COMPANY_INFO.brandName,
        legalName: COMPANY_INFO.legalName,
        url: COMPANY_INFO.websiteUrl,
        email: COMPANY_INFO.contactEmail,
        taxID: COMPANY_INFO.taxId,
        address: {
          "@type": "PostalAddress",
          streetAddress: COMPANY_INFO.registeredAddress,
          addressCountry: "RO",
        },
        contactPoint: {
          "@type": "ContactPoint",
          email: COMPANY_INFO.contactEmail,
          contactType: "customer support",
          areaServed: "RO",
          availableLanguage: ["Romanian"],
        },
      });
      document.head.appendChild(script);
    }

    // 3) SoftwareApplication JSON-LD with dynamic offers from DB.
    //    Re-run whenever tiers load. Skip during BETA (placeholder prices).
    document.getElementById(APP_SCRIPT_ID)?.remove();
    if (!BETA_MODE && tiers && tiers.length > 0) {
      const prices = tiers.map((t) => Number(t.price_ron) || 0);
      const lowPrice = Math.min(...prices);
      const highPrice = Math.max(...prices);

      const offers =
        tiers.length === 1
          ? {
              "@type": "Offer",
              name: tiers[0].name,
              price: prices[0].toFixed(2),
              priceCurrency: "RON",
              url: `${COMPANY_INFO.websiteUrl}/select-plan`,
              availability: "https://schema.org/InStock",
            }
          : {
              "@type": "AggregateOffer",
              priceCurrency: "RON",
              lowPrice: lowPrice.toFixed(2),
              highPrice: highPrice.toFixed(2),
              priceRange: `${lowPrice.toFixed(0)}–${highPrice.toFixed(0)} RON`,
              offerCount: tiers.length,
              offers: tiers.map((t, i) => ({
                "@type": "Offer",
                name: t.name,
                price: prices[i].toFixed(2),
                priceCurrency: "RON",
                url: `${COMPANY_INFO.websiteUrl}/select-plan`,
                availability: "https://schema.org/InStock",
              })),
            };

      // aggregateRating — emit ONLY if we have at least 1 approved review.
      // Faking this risks Google manual penalties for "spammy structured markup".
      const aggregateRating =
        reviews && reviews.length > 0
          ? {
              aggregateRating: {
                "@type": "AggregateRating",
                ratingValue: (
                  reviews.reduce((s, r) => s + Number(r.rating), 0) /
                  reviews.length
                ).toFixed(1),
                reviewCount: reviews.length,
                bestRating: "5",
                worstRating: "1",
              },
            }
          : {};

      const appScript = document.createElement("script");
      appScript.type = "application/ld+json";
      appScript.id = APP_SCRIPT_ID;
      appScript.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: COMPANY_INFO.brandName,
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web, iOS, Android",
        url: COMPANY_INFO.websiteUrl,
        inLanguage: "ro-RO",
        offers,
        ...aggregateRating,
      });
      document.head.appendChild(appScript);
    }

    return () => {
      document.getElementById(ORG_SCRIPT_ID)?.remove();
      document.getElementById(APP_SCRIPT_ID)?.remove();
    };
  }, [tiers, reviews]);

  return null;
};

export default SeoHead;
